
import {
  Vue, Component, Inject, Ref,
} from 'vue-property-decorator';
import eventBus from '@/features/common/eventBus';
import eventNames from '@/features/common/constants/events';
import {
  BillingType,
  CarrierCodeType,
  InProgress,
  ShipFrom, ShipTo,
} from '@/features/shipment/types';
import { CarriersType } from '@/features/common/raw';
import { carrierAccountRules, requiredRules } from '@/features/common/helpers/validationRules';
import { VForm } from '@/features/common/types';
import PackagesDetail from '../PackagesDetail.vue';
import ShippingAddress from '../ShippingAddress.vue';

@Component({
  components: {
    ShippingAddress,
    PackagesDetail,
  },
})
export default class ShippingDetails extends Vue {
  @Inject({ default: false }) readonly isEstimatePage!: boolean;

  @Ref('formReceiversAccount') formReceiversAccount!: VForm;

  private billingType: keyof typeof BillingType = BillingType.Prepaid;

  private carrierInfo: { carrier: keyof typeof CarrierCodeType | string, accountNum: string } = {
    carrier: '',
    accountNum: '',
  }

  private carriers: CarriersType = [
    'UPS',
    'FedEx',
    'Purolator',
    'GLS',
  ];

  private requiredRules = requiredRules;

  private carrierAccountRules = carrierAccountRules;

  get fromAddress(): ShipFrom {
    if (this.isEstimatePage) {
      return this.$store.state.estimate.estimateData.fromAddress;
    }
    return this.$store.getters.getShipFromInProgress;
  }

  get toAddress(): ShipTo {
    if (this.isEstimatePage) {
      return this.$store.state.estimate.estimateData.toAddress;
    }
    return this.$store.getters.getShipToInProgress;
  }

  get inProgressData(): InProgress {
    return this.$store.getters.getInProgress;
  }

  get isPrepaid(): boolean {
    return this.billingType === BillingType.Prepaid;
  }

  get showDutiesAndTaxes(): boolean {
    return this.billingType !== 'FreightCollect'
      && this.fromAddress.countryCode !== this.toAddress.countryCode;
  }

  get isDisableHideDetailsOption(): boolean {
    if (this.isEstimatePage) return false;

    return this.billingType === BillingType.FreightCollect
      && (!this.carrierInfo.accountNum || !this.carrierInfo.carrier);
  }

  get carrierAccountCharacterLimit(): number | boolean {
    switch (this.carrierInfo.carrier) {
      case 'UPS':
        return 6;
      case 'FedEx':
        return 9;
      default:
        return false;
    }
  }

  mounted(): void {
    eventBus.$on(
      eventNames.VALIDATE_RECEIVERS_ACCOUNT_DETAILS,
      this.validateReceiversAccountDetails,
    );

    if (!this.inProgressData) return;

    this.billingType = this.inProgressData.billing.billingType as BillingType;
    this.carrierInfo.accountNum = this.inProgressData.billing.account || '';
    this.carrierInfo.carrier = this.inProgressData.carrierCode;
  }

  // eslint-disable-next-line class-methods-use-this
  destroyed(): void {
    eventBus.$off(eventNames.VALIDATE_RECEIVERS_ACCOUNT_DETAILS);
  }

  private hideDetails = () => eventBus.$emit(eventNames.HIDE_ESTIMATE_RESULTS_DETAIL);

  private handleChangeBillingType(val, isTaxes): void {
    this.carrierInfo.carrier = '';
    this.carrierInfo.accountNum = '';
    this.patchBillingInProgress({ key: 'account', value: '' });
    this.$store.commit('patchInProgress', { key: 'carrierCode', value: '' });

    if (!val) {
      eventBus.$emit(eventNames.TOGGLE_RECEIVERS_ACCOUNT, false);
    }

    if (isTaxes) {
      this.billingType = val ? BillingType.FreeDomicile : BillingType.Prepaid;
    } else {
      this.billingType = val ? BillingType.FreightCollect : BillingType.Prepaid;
      eventBus.$emit(eventNames.TOGGLE_RECEIVERS_ACCOUNT, val);
      if (val) {
        this.resetReceiversAccountValidation();
      }
    }

    this.patchBillingInProgress({ key: 'billingType', value: this.billingType }, true);
  }

  private handleCarrierChange() {
    this.$store.commit('patchInProgress', { key: 'carrierCode', value: this.carrierInfo.carrier });
    this.$store.dispatch('syncInProgressData');
    eventBus.$emit(eventNames.TOGGLE_RECEIVERS_ACCOUNT, true);
  }

  private patchBillingInProgress({ key, value }, isSync = false) {
    const billing = { ...this.$store.getters.getInProgress.billing };
    billing[key] = value;

    this.$store.commit('patchInProgress', {
      key: 'billing',
      value: billing,
    });

    if (!isSync) return;
    this.$store.dispatch('syncInProgressData');
  }

  private async validateReceiversAccountDetails() {
    await this.$nextTick();
    this.formReceiversAccount.validate();
  }

  private async resetReceiversAccountValidation(): Promise<void> {
    await this.$nextTick();
    this.formReceiversAccount.resetValidation();
  }
}
